/**
 * 翻转大富翁 — 等距坐标变换工具模块
 *
 * 封装等距渲染的核心数学逻辑：坐标变换、深度排序、碰撞检测、矢量绘制。
 * 这是整个等距渲染的基础设施模块，不依赖任何其他项目文件，只依赖 pixi.js。
 */

import * as PIXI from 'pixi.js';

// ── 等距常量 ──

/** 等距菱形宽度（与 BoardRenderer.cellSize 一致） */
export const TILE_WIDTH = 155;

/** 等距菱形高度 = TILE_WIDTH / 2（2:1 等距比例） */
export const TILE_HEIGHT = 78;

/** 等距角度 ~26.57° */
export const ISO_ANGLE = Math.atan(0.5);

// ── 深度层枚举 ──

export type IsoLayer = 'tile' | 'building' | 'player';

// ── 配置接口 ──

export interface IsoConfig {
  tileWidth: number;
  tileHeight: number;
}

// ── 工具类 ──

/**
 * 等距坐标变换工具类
 *
 * 提供 Cartesian ↔ Isometric 坐标转换、深度排序值计算、
 * 矢量菱形/立体方块绘制、碰撞检测等功能。
 */
export class IsometricUtils {
  readonly tileWidth: number;
  readonly tileHeight: number;

  constructor(config?: Partial<IsoConfig>) {
    this.tileWidth = config?.tileWidth ?? TILE_WIDTH;
    this.tileHeight = config?.tileHeight ?? TILE_HEIGHT;
  }

  /**
   * 像素直角坐标 → 等距屏幕坐标
   *
   * 输入为像素坐标（来自 getCellPos()），内部先转换为网格坐标再投影。
   * 公式（先转网格再等距投影）：
   *   gridX = cartX / tileWidth
   *   gridY = cartY / tileWidth
   *   screenX = (gridX - gridY) * (tileWidth / 2) = (cartX - cartY) / 2
   *   screenY = (gridX + gridY) * (tileHeight / 2) = (cartX + cartY) * tileHeight / (2 * tileWidth)
   *
   * @param cartX  直角像素坐标 X（来自 getCellPos）
   * @param cartY  直角像素坐标 Y
   * @returns      等距屏幕坐标 { x, y }
   */
  toIso(cartX: number, cartY: number): { x: number; y: number } {
    // 先将像素坐标转为网格坐标
    const gridX = cartX / this.tileWidth;
    const gridY = cartY / this.tileWidth;
    // 应用标准等距投影
    return {
      x: (gridX - gridY) * (this.tileWidth / 2),
      y: (gridX + gridY) * (this.tileHeight / 2),
    };
  }

  /**
   * 等距屏幕坐标 → 像素直角坐标（逆变换，用于点击命中检测）
   *
   * 公式（从屏幕坐标逆推网格坐标，再转回像素）：
   *   gridX = screenX / (tileWidth / 2) + screenY / (tileHeight / 2) 的一半
   *   gridY = screenY / (tileHeight / 2) - screenX / (tileWidth / 2) 的一半
   *   cartX = gridX * tileWidth
   *   cartY = gridY * tileWidth
   *
   * 简化后：
   *   cartX = screenX + tileWidth * screenY / tileHeight
   *   cartY = tileWidth * screenY / tileHeight - screenX
   *
   * @param screenX  等距屏幕坐标 X
   * @param screenY  等距屏幕坐标 Y
   * @returns        直角像素坐标 { x, y }
   */
  fromIso(screenX: number, screenY: number): { x: number; y: number } {
    // 逆投影：屏幕坐标 → 网格坐标
    const gridX = screenX / this.tileWidth + screenY / this.tileHeight;
    const gridY = screenY / this.tileHeight - screenX / this.tileWidth;
    // 网格坐标 → 像素坐标
    return {
      x: gridX * this.tileWidth,
      y: gridY * this.tileWidth,
    };
  }

  /**
   * 计算等距深度值（用于 PixiJS zIndex 排序）
   *
   * 等距视角下，行+列越大 = 越靠前 = zIndex 越大。
   *
   * @param gridX  直角坐标 X（像素值，需除以 cellSize 得到网格坐标）
   * @param gridY  直角坐标 Y
   * @param layer  层级：tile(基础) / building(+0.3) / player(+0.5)
   * @returns      zIndex 值（浮点数，支持同格内多层微调）
   */
  getDepth(gridX: number, gridY: number, layer: IsoLayer = 'tile'): number {
    // 网格坐标 = 像素坐标 / cellSize（cellSize = tileWidth）
    const col = gridX / this.tileWidth;
    const row = gridY / this.tileWidth;
    const base = col + row;
    switch (layer) {
      case 'tile':
        return base;
      case 'building':
        return base + 0.3;
      case 'player':
        return base + 0.5;
    }
  }

  /**
   * 绘制等距菱形底座（用于矢量地块背景）
   *
   * 以 (cx, cy) 为菱形中心点，绘制 4 个顶点连成的菱形。
   * 顶点顺序：上 → 右 → 下 → 左
   *   top:    (cx, cy - tileHeight/2)
   *   right:  (cx + tileWidth/2, cy)
   *   bottom: (cx, cy + tileHeight/2)
   *   left:   (cx - tileWidth/2, cy)
   *
   * @param g      PIXI.Graphics 实例
   * @param cx     菱形中心 X
   * @param cy     菱形中心 Y
   * @param fill   填充颜色（0xRRGGBB）
   * @param alpha  填充透明度（0-1），默认 1
   */
  drawDiamond(
    g: PIXI.Graphics,
    cx: number,
    cy: number,
    fill: number,
    alpha: number = 1,
  ): void {
    const hw = this.tileWidth / 2;
    const hh = this.tileHeight / 2;
    g.moveTo(cx, cy - hh);
    g.lineTo(cx + hw, cy);
    g.lineTo(cx, cy + hh);
    g.lineTo(cx - hw, cy);
    g.closePath();
    g.beginFill(fill, alpha);
    g.endFill();
  }

  /**
   * 绘制等距立体方块（顶面 + 左侧面 + 右侧面）
   *
   * 用于矢量等距地块的三维效果。绘制顺序：左侧面 → 右侧面 → 顶面
   * （确保顶面在最上层，遮挡侧面的上边缘）。
   *
   * @param g           PIXI.Graphics 实例
   * @param cx          菱形中心 X
   * @param cy          菱形中心 Y
   * @param topColor    顶面颜色
   * @param leftColor   左侧面颜色（通常比 topColor 暗）
   * @param rightColor  右侧面颜色（通常比 topColor 略暗）
   * @param height      方块厚度（像素，建议 30-40），默认 35
   */
  drawIsoBlock(
    g: PIXI.Graphics,
    cx: number,
    cy: number,
    topColor: number,
    leftColor: number,
    rightColor: number,
    height: number = 35,
  ): void {
    const hw = this.tileWidth / 2;
    const hh = this.tileHeight / 2;

    // 左侧面（平行四边形）+ 深色描边增强轮廓
    g.lineStyle(1.5, 0x000000, 0.3);
    g.beginFill(leftColor, 1);
    g.moveTo(cx - hw, cy);
    g.lineTo(cx, cy + hh);
    g.lineTo(cx, cy + hh + height);
    g.lineTo(cx - hw, cy + height);
    g.closePath();
    g.endFill();

    // 右侧面（平行四边形）+ 深色描边增强轮廓
    g.lineStyle(1.5, 0x000000, 0.3);
    g.beginFill(rightColor, 1);
    g.moveTo(cx + hw, cy);
    g.lineTo(cx, cy + hh);
    g.lineTo(cx, cy + hh + height);
    g.lineTo(cx + hw, cy + height);
    g.closePath();
    g.endFill();

    // 顶面（菱形）+ 深色描边增强轮廓
    g.lineStyle(1.5, 0x000000, 0.3);
    g.beginFill(topColor, 1);
    g.moveTo(cx, cy - hh);
    g.lineTo(cx + hw, cy);
    g.lineTo(cx, cy + hh);
    g.lineTo(cx - hw, cy);
    g.closePath();
    g.endFill();

    // 重置线宽，避免影响后续绘制
    g.lineStyle(0);
  }

  /**
   * 计算等距棋盘的外接矩形（用于 centerBoard 居中计算）
   *
   * 等距菱形棋盘的外接矩形：
   *   宽 = (gridCols + gridRows) * tileWidth / 2
   *   高 = (gridCols + gridRows) * tileHeight / 2
   *
   * @param gridCols  网格列数（本项目 11）
   * @param gridRows  网格行数（本项目 11）
   * @returns         { width, height } 外接矩形尺寸
   */
  getBoundingBox(gridCols: number, gridRows: number): { width: number; height: number } {
    return {
      width: ((gridCols + gridRows) * this.tileWidth) / 2,
      height: ((gridCols + gridRows) * this.tileHeight) / 2,
    };
  }

  /**
   * 点是否在等距菱形内（精确碰撞检测，备用）
   *
   * 通过菱形方程判断：|dx/hw| + |dy/hh| <= 1
   *
   * @param px  屏幕点 X（相对于菱形中心）
   * @param py  屏幕点 Y（相对于菱形中心）
   * @param cx  菱形中心 X
   * @param cy  菱形中心 Y
   * @returns   是否在菱形内
   */
  isPointInDiamond(px: number, py: number, cx: number, cy: number): boolean {
    // 相对坐标
    const dx = px - cx;
    const dy = py - cy;
    const hw = this.tileWidth / 2;
    const hh = this.tileHeight / 2;
    // 菱形方程：|dx/hw| + |dy/hh| <= 1
    return Math.abs(dx / hw) + Math.abs(dy / hh) <= 1;
  }
}

// ── 单例导出（全局共享同一套等距参数）──

export const isoUtils = new IsometricUtils();
